import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Share, PlusSquare, Download, Smartphone, CheckCircle2 } from "lucide-react";
import { useInstallState, promptInstall, isInstalled, canPrompt as browserCanPrompt, openInstall as open } from "@/lib/install";

const KEY = "dmv_install_dismissed_at";

/** Footer / menu button: use the browser's install sheet when it is available, otherwise show the steps. */
export const openInstall = () => (browserCanPrompt() ? promptInstall() : open());

export default function InstallPrompt() {
  const { canPrompt, ios, installed } = useInstallState();
  const [show, setShow] = useState(false);
  const [manual, setManual] = useState(false);

  useEffect(() => {
    let dismissed = 0;
    try { dismissed = Number(localStorage.getItem(KEY) || 0); } catch {}
    const recentlyDismissed = Date.now() - dismissed < 14 * 86400000;
    let t;
    if (!recentlyDismissed && !isInstalled()) t = setTimeout(() => setShow(true), ios ? 20000 : 15000);
    const onOpen = () => { setManual(true); setShow(true); };
    window.addEventListener("dmv:install", onOpen);
    return () => { window.removeEventListener("dmv:install", onOpen); clearTimeout(t); };
  }, [ios]);

  const dismiss = () => { setShow(false); setManual(false); try { localStorage.setItem(KEY, String(Date.now())); } catch {} };
  const install = async () => { await promptInstall(); setShow(false); setManual(false); };

  const visible = show && (manual || ((canPrompt || ios) && !installed));

  return (
    <AnimatePresence>
      {visible && (
        <motion.div initial={{ y: 120, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 120, opacity: 0 }} transition={{ type: "spring", damping: 24, stiffness: 260 }}
          className="fixed z-[70] left-3 right-[84px] sm:right-auto sm:w-[360px] md:left-6" style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)" }} data-testid="install-prompt" role="dialog" aria-label="Install the DartMyVenue app">
          <div className="rounded-2xl bg-white border border-[color:var(--line)] shadow-[0_24px_60px_rgba(42,15,17,0.25)] p-4">
            <div className="flex items-start gap-3">
              <img src="/icons/icon-192.png" alt="" className="w-12 h-12 rounded-xl border border-[color:var(--line)]"/>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[color:var(--ink)] leading-tight">Get the DartMyVenue app</div>
                <div className="text-sm text-neutral-600 leading-snug mt-0.5">Opens full-screen from your home screen, loads faster and keeps you signed in.</div>
              </div>
              <button onClick={dismiss} className="p-1.5 -m-1 rounded-full hover:bg-[color:var(--ivory)] text-neutral-500" aria-label="Close"><X className="w-4 h-4"/></button>
            </div>
            {installed ? (
              <div className="mt-3 text-sm text-[color:var(--ink-2)] flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600"/> The app is already on this device. Open it from your home screen.</div>
            ) : canPrompt ? (
              <button onClick={install} className="btn-wine w-full mt-3 !py-2.5" data-testid="install-btn"><Download className="w-4 h-4"/> Install app</button>
            ) : ios ? (
              <ol className="mt-3 space-y-2 text-sm text-[color:var(--ink-2)]">
                <li className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[color:var(--ivory)] text-[color:var(--maroon)] text-xs font-bold flex items-center justify-center">1</span> Tap <Share className="w-4 h-4 text-[color:var(--maroon)]"/> <b>Share</b> in Safari's toolbar</li>
                <li className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[color:var(--ivory)] text-[color:var(--maroon)] text-xs font-bold flex items-center justify-center">2</span> Choose <PlusSquare className="w-4 h-4 text-[color:var(--maroon)]"/> <b>Add to Home Screen</b></li>
              </ol>
            ) : (
              <div className="mt-3 text-sm text-[color:var(--ink-2)] flex items-start gap-2"><Smartphone className="w-4 h-4 mt-0.5 text-[color:var(--maroon)] shrink-0"/> Open your browser menu (⋮) and choose <b>Install app</b> or <b>Add to Home screen</b>.</div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
